
import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  CreditCard,
  Plus,
  Search,
  Download,
  Receipt,
  CheckCircle,
  Clock,
  AlertCircle,
  Calendar
} from "lucide-react";
import { db, auth } from "@/firebase/config";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { getCurrentTerm, type TermInfo, type Term } from "@/utils/feeCalculations";

type TxStatus = "pending" | "completed" | "failed";
type ProviderId = "mpesa" | "equity" | "kcb" | "cash" | "card";
interface PaymentRecord {
  id: string;
  studentName?: string;
  amount: number;
  provider: ProviderId;
  status: TxStatus;
  transactionDate?: any; // Firestore Timestamp | undefined
  reference?: string;
  accountReference?: string;
  phoneNumber?: string;
  // Term tracking
  paymentForTerm?: 1 | 2 | 3;
  paymentForYear?: number;
  termKey?: string;
  termLabel?: string;
}

const PaymentProcessing = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [recentPayments, setRecentPayments] = useState<PaymentRecord[]>([]);
  const [loadingPayments, setLoadingPayments] = useState<boolean>(false);
  const [regNameMap, setRegNameMap] = useState<Record<string, string>>({});
  const [paymentMethods, setPaymentMethods] = useState<
    { id: "bank" | "mobile" | "cash" | "card"; name: string; icon: string; enabled: boolean }[]
  >([
    { id: "bank", name: "Bank Transfer", icon: "🏦", enabled: false },
    { id: "mobile", name: "Mobile Money", icon: "📱", enabled: false },
    { id: "cash", name: "Cash", icon: "💵", enabled: true },
    { id: "card", name: "Credit/Debit Card", icon: "💳", enabled: false },
  ]);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [debugLogs, setDebugLogs] = useState<string[]>([]);
  const addDebugLog = (message: string, data?: any) => {
    const ts = new Date().toISOString();
    setDebugLogs((prev) => [
      ...prev,
      data !== undefined ? `[${ts}] ${message}\n${JSON.stringify(data, null, 2)}` : `[${ts}] ${message}`,
    ]);
  };

  // Form state for Record Payment dialog
  const [formStudent, setFormStudent] = useState<string>("");
  const [formAmount, setFormAmount] = useState<string>("");
  const [formMethod, setFormMethod] = useState<"bank" | "mobile" | "cash" | "card" | "">("");
  const [formReference, setFormReference] = useState<string>("");
  const [formAccountReference, setFormAccountReference] = useState<string>("");
  const [formTermKey, setFormTermKey] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);
  
  // Term tracking
  const [currentTerm, setCurrentTerm] = useState<TermInfo | null>(null);
  const [availableTerms, setAvailableTerms] = useState<Array<{ key: string; label: string; termNumber: 1 | 2 | 3; year: number }>>([]); 

  // Resolve provider label by provider id
  const providerLabel = (p: ProviderId): string => {
    if (p === "mpesa") return "Mobile Money";
    if (p === "equity" || p === "kcb") return "Bank Transfer";
    if (p === "cash") return "Cash";
    if (p === "card") return "Credit/Debit Card";
    return p;
  };

  // Auth: get schoolID from user claims similar to AdminFinance
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        setSchoolID(null);
        return;
      }
      try {
        const idTokenResult = await currentUser.getIdTokenResult(true);
        const affiliations = (idTokenResult.claims.affiliations as any[]) || [];
        const defAff = idTokenResult.claims.defaultAffiliation as any;
        let sid: string | null = null;
        // Prefer an actual school-bound role; avoid owner placeholder ids like 'cbsms-owner'
        const pickSchoolIdFrom = (list: any[], role: string) => list.find((a) => a.role === role && a.schoolID && a.schoolID !== 'cbsms-owner')?.schoolID as string | undefined;
        if (defAff?.role === "admin" && defAff.schoolID && defAff.schoolID !== 'cbsms-owner') sid = defAff.schoolID as string;
        if (!sid && affiliations.length) sid = pickSchoolIdFrom(affiliations, 'admin') || pickSchoolIdFrom(affiliations, 'school_staff') || undefined || null;
        if (!sid && defAff?.schoolID && defAff.schoolID !== 'cbsms-owner') sid = defAff.schoolID as string;
        if (!sid && affiliations.length) sid = (affiliations.find((a) => a.schoolID && a.schoolID !== 'cbsms-owner')?.schoolID as string) || null;
        setSchoolID(sid);
        addDebugLog("Auth derived schoolID", { schoolID: sid, affiliationsCount: affiliations.length, hasDefault: !!defAff });
      } finally {
        // no-op
      }
    });
    return () => unsub();
  }, []);

  // Load payment methods from Firestore paymentConfig
  useEffect(() => {
    const loadPaymentConfig = async () => {
      if (!schoolID) return;
      try {
        const cfgSnap = await getDoc(doc(db, "schools", schoolID, "paymentConfig", "main"));
        const data = cfgSnap.exists() ? cfgSnap.data() : null;
        const methods = (data?.paymentMethods as any[]) || [];
        // Determine availability
        const hasMpesa = methods.some((m) => m.provider === "mpesa");
        const hasEquity = methods.some((m) => m.provider === "equity");
        const hasKcb = methods.some((m) => m.provider === "kcb");
        const hasCard = methods.some((m) => m.provider === "card");
        const hasCash = methods.some((m) => m.provider === "cash");
        setPaymentMethods([
          { id: "bank", name: "Bank Transfer", icon: "🏦", enabled: hasEquity || hasKcb },
          { id: "mobile", name: "Mobile Money", icon: "📱", enabled: hasMpesa },
          { id: "cash", name: "Cash", icon: "💵", enabled: hasCash || true },
          { id: "card", name: "Credit/Debit Card", icon: "💳", enabled: hasCard || false },
        ]);
        addDebugLog("Loaded paymentConfig", { hasMpesa, hasEquity, hasKcb, hasCard, hasCash });
      } catch (e) {
        // keep defaults
        addDebugLog("Failed to load paymentConfig", { error: String(e) });
      }
    };
    loadPaymentConfig();
  }, [schoolID]);

  // Load current term and generate available terms for selection
  useEffect(() => {
    const loadTerms = async () => {
      if (!schoolID) return;
      try {
        // Fetch terms from Firestore
        const termsRef = collection(db, `schools/${schoolID}/terms`);
        const termsSnap = await getDocs(termsRef);
        const termsList = termsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Term));
        
        const term = getCurrentTerm(termsList);
        
        if (term) {
          setCurrentTerm(term);
          setFormTermKey(term.termKey || ""); // Default to current term
          
          // Generate available terms: current term and surrounding terms
          const terms: Array<{ key: string; label: string; termNumber: 1 | 2 | 3; year: number }> = [];
          const currentYear = term.year || new Date().getFullYear();
          
          // Previous year's term 3 (for late payments)
          terms.push({
            key: `term3_${currentYear - 1}`,
            label: `Term 3 ${currentYear - 1}`,
            termNumber: 3,
            year: currentYear - 1
          });
          
          // All terms of current year
          for (const termNum of [1, 2, 3] as const) {
            terms.push({
              key: `term${termNum}_${currentYear}`,
              label: `Term ${termNum} ${currentYear}`,
              termNumber: termNum,
              year: currentYear
            });
          }
          
          // Next year's term 1 (for advance payments)
          terms.push({
            key: `term1_${currentYear + 1}`,
            label: `Term 1 ${currentYear + 1}`,
            termNumber: 1,
            year: currentYear + 1
          });
          
          setAvailableTerms(terms);
          addDebugLog("Loaded terms", { currentTerm: term, availableTerms: terms.length });
        }
      } catch (e) {
        addDebugLog("Failed to load terms", { error: String(e) });
      }
    };
    loadTerms();
  }, [schoolID]);

  // Load recent payments from provider subcollections and merge
  useEffect(() => {
    const loadRecent = async () => {
      if (!schoolID) return;
      setLoadingPayments(true);
      try {
        const providers: ProviderId[] = ["mpesa", "equity", "kcb", "cash", "card"];
        const results: PaymentRecord[] = [];
        for (const p of providers) {
          try {
            // Use a simple limited query and sort client-side by best-available timestamp to avoid index/order issues
            const qRef = query(collection(db, `schools/${schoolID}/transactions/${p}/records`), limit(20));
            const snap = await getDocs(qRef);
            addDebugLog("Fetched provider transactions", { provider: p, count: snap.size });
            snap.forEach((d) => {
              const data = d.data() as any;
              results.push({
                id: d.id,
                studentName: data.studentName,
                amount: data.amount ?? 0,
                provider: p,
                status: (data.status ?? "completed") as TxStatus,
                transactionDate: data.transactionDate || data.lastUpdate || data.createdAt || data.timestamp || data?.mpesaDetails?.transactionDate,
                reference: data.mpesaDetails?.receiptNumber || data.equityDetails?.transactionReference || data.kcbDetails?.transactionReference || data.reference,
                accountReference: data.accountReference || data.mpesaDetails?.accountReference,
                phoneNumber: data.phoneNumber || data.mpesaDetails?.phoneNumber,
              });
            });
          } catch (e: any) {
            // ignore provider failures to keep page usable
              addDebugLog("Provider fetch failed", { provider: p, error: String(e?.message || e) });
          }
        }
        // sort by date desc
        const getMs = (r: PaymentRecord) => {
          const t = r.transactionDate;
          if (!t) return 0;
          try { if (typeof t.toMillis === 'function') return t.toMillis(); } catch {}
          try { if (typeof t.toDate === 'function') return t.toDate().getTime(); } catch {}
          if (typeof t === 'string') { const d = new Date(t); return isNaN(d.getTime()) ? 0 : d.getTime(); }
          if (t instanceof Date) return t.getTime();
          return 0;
        };
        results.sort((a, b) => getMs(b) - getMs(a));
        setRecentPayments(results.slice(0, 15));
        addDebugLog("Merged recent payments", { total: results.length, shown: Math.min(15, results.length) });
      } finally {
        setLoadingPayments(false);
      }
    };
    loadRecent();
  }, [schoolID]);

  // Resolve student names from accountReference pattern Fee-<registrationNumber>
  useEffect(() => {
    const fetchStudentNames = async () => {
      if (!schoolID || recentPayments.length === 0) return;
      // Collect unique registration numbers from accountReference
      const regs = Array.from(
        new Set(
          recentPayments
            .map((p) => p.accountReference)
            .filter(Boolean)
            .map((ar) => {
              const m = /^Fee-(.+)$/i.exec(ar as string);
              return m ? m[1] : null;
            })
            .filter(Boolean) as string[]
        )
      );
      if (regs.length === 0) return;
      // Firestore 'in' supports up to 10 values; chunk requests
      const chunk = <T,>(arr: T[], size: number) => arr.reduce((acc: T[][], _, i) => (i % size ? acc : [...acc, arr.slice(i, i + size)]), [] as T[][]);
      const chunks = chunk(regs, 10);
      const map: Record<string, string> = {};
      try {
        for (const group of chunks) {
          const qRef = query(collection(db, `schools/${schoolID}/students`), where("registrationNumber", "in", group as any));
          const snap = await getDocs(qRef);
          snap.forEach((doc) => {
            const data = doc.data() as any;
            const rn = data.registrationNumber as string;
            const name = `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.fullName || doc.id;
            if (rn) map[rn] = name;
          });
        }
        setRegNameMap((prev) => ({ ...prev, ...map }));
      } catch (e) {
        // best-effort; ignore mapping errors
      }
    };
    fetchStudentNames();
  }, [recentPayments, schoolID]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
          <CheckCircle className="h-3 w-3 mr-1" />
          Completed
        </Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
          <Clock className="h-3 w-3 mr-1" />
          Pending
        </Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
          <AlertCircle className="h-3 w-3 mr-1" />
          Failed
        </Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  // Filtered table rows based on search term
  const filteredPayments = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return recentPayments;
    return recentPayments.filter((p) => {
      const dateStr = p.transactionDate?.toDate ? p.transactionDate.toDate().toISOString().slice(0, 10) : "";
      return (
        p.id.toLowerCase().includes(term) ||
        (p.studentName || "").toLowerCase().includes(term) ||
        providerLabel(p.provider).toLowerCase().includes(term) ||
        (p.reference || "").toLowerCase().includes(term) ||
        dateStr.includes(term)
      );
    });
  }, [recentPayments, searchTerm]);

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div>
          <h2 className="text-lg font-semibold">Payment Processing</h2>
          <p className="text-sm text-muted-foreground">Process payments and manage payment methods</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowDebugPanel((v) => !v)}>
            {showDebugPanel ? "Hide Debug" : "Debug"}
          </Button>
          <Dialog>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Record Payment
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Record New Payment</DialogTitle>
                <DialogDescription>Add a new payment record</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="student">Student</Label>
                  <Input id="student" placeholder="Enter student name (optional)" value={formStudent} onChange={(e) => setFormStudent(e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="amount">Amount</Label>
                    <Input id="amount" type="number" placeholder="5000" value={formAmount} onChange={(e) => setFormAmount(e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="method">Payment Method</Label>
                    <Select value={formMethod} onValueChange={(v: any) => setFormMethod(v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select method" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bank">Bank Transfer</SelectItem>
                        <SelectItem value="mobile">Mobile Money</SelectItem>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="card">Card</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="accountReference">Account Reference</Label>
                  <Input
                    id="accountReference"
                    placeholder="Fee-REG1234 (use student's registration number)"
                    value={formAccountReference}
                    onChange={(e) => setFormAccountReference(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Use the format Fee-{`<registrationNumber>`} to link this payment to a student.</p>
                </div>
                <div>
                  <Label htmlFor="paymentTerm" className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Payment For Term
                  </Label>
                  <Select value={formTermKey} onValueChange={setFormTermKey}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select term" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTerms.map((term) => (
                        <SelectItem key={term.key} value={term.key}>
                          {term.label} {currentTerm?.termKey === term.key && "(Current)"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    This payment will be permanently recorded for the selected term.
                  </p>
                </div>
                <div>
                  <Label htmlFor="reference">Reference Number</Label>
                  <Input id="reference" placeholder="Payment reference" value={formReference} onChange={(e) => setFormReference(e.target.value)} />
                </div>
                <Button
                  className="w-full"
                  disabled={!schoolID || !formAmount || !formMethod || !formAccountReference || !formTermKey || saving}
                  onClick={async () => {
                    if (!schoolID || !formMethod || !formTermKey) return;
                    setSaving(true);
                    try {
                      const amountNum = Number(formAmount);
                      if (Number.isNaN(amountNum) || amountNum <= 0) {
                        setSaving(false);
                        return;
                      }
                      
                      // Find selected term info
                      const selectedTerm = availableTerms.find(t => t.key === formTermKey);
                      if (!selectedTerm) {
                        addDebugLog("Selected term not found", { formTermKey });
                        setSaving(false);
                        return;
                      }
                      
                      // Map UI method to provider collection
                      let provider: ProviderId = "cash";
                      if (formMethod === "mobile") provider = "mpesa";
                      else if (formMethod === "bank") {
                        // choose bank provider by config: prefer equity, else kcb
                        try {
                          const cfg = await getDoc(doc(db, "schools", schoolID, "paymentConfig", "main"));
                          const methods = (cfg.exists() ? (cfg.data()?.paymentMethods || []) : []) as any[];
                          const hasEquity = methods.some((m) => m.provider === "equity");
                          const hasKcb = methods.some((m) => m.provider === "kcb");
                          provider = (hasEquity ? "equity" : hasKcb ? "kcb" : "equity") as ProviderId;
                        } catch {
                          provider = "equity"; // fallback
                        }
                      } else if (formMethod === "cash") provider = "cash";
                      else if (formMethod === "card") provider = "card";

                      const colRef = collection(db, `schools/${schoolID}/transactions/${provider}/records`);
                      await addDoc(colRef, {
                        studentName: formStudent || undefined,
                        amount: amountNum,
                        status: "completed",
                        transactionDate: serverTimestamp(),
                        reference: formReference || formAccountReference || undefined,
                        accountReference: formAccountReference || undefined,
                        createdBy: "admin-ui",
                        // Term tracking - locked at payment time
                        paymentForTerm: selectedTerm.termNumber,
                        paymentForYear: selectedTerm.year,
                        termKey: selectedTerm.key,
                        termLabel: selectedTerm.label,
                      });
                      
                      addDebugLog("Payment recorded with term", { 
                        amount: amountNum, 
                        provider, 
                        termKey: selectedTerm.key,
                        accountReference: formAccountReference 
                      });
                      
                      // Refresh list
                      setFormStudent("");
                      setFormAmount("");
                      setFormMethod("");
                      setFormReference("");
                      setFormAccountReference("");
                      // Keep formTermKey as current term for next entry
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  {saving ? "Saving..." : "Record Payment"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>
      {showDebugPanel && (
        <div className="fixed bottom-0 right-0 z-50 w-full max-w-xl bg-black text-white p-4 rounded-t-lg shadow-lg overflow-y-auto max-h-96 border-t-4 border-blue-500">
          <div className="flex justify-between items-center mb-2">
            <span className="font-bold">Debug Logs</span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigator.clipboard?.writeText(debugLogs.join("\n\n"))}
              >
                Copy
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowDebugPanel(false)}>
                Close
              </Button>
            </div>
          </div>
          <pre className="text-xs whitespace-pre-wrap max-h-80 overflow-y-auto">{debugLogs.join("\n\n") || "No logs yet."}</pre>
        </div>
      )}

      {/* Payment Methods Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Payment Methods
          </CardTitle>
          <CardDescription>Configure available payment methods</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {paymentMethods.map((method) => (
              <div
                key={method.id}
                className="flex items-center justify-between p-4 border rounded-lg border-[#E5E7EB] dark:border-[#364153]"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{method.icon}</span>
                  <span className="font-medium">{method.name}</span>
                </div>
                <Badge variant={method.enabled ? "default" : "secondary"}>
                  {method.enabled ? "Active" : "Inactive"}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Payments */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div>
              <CardTitle>Recent Payments</CardTitle>
              <CardDescription>Latest payment transactions</CardDescription>
            </div>
            <div className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search payments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full sm:w-64"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Payment ID</TableHead>
                  <TableHead>Student</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingPayments ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                      <div className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5 text-muted-foreground" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                        </svg>
                        <span>Loading payments…</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredPayments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>No payments found.</TableCell>
                  </TableRow>
                ) : (
                  filteredPayments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell className="font-medium">{payment.id}</TableCell>
                      <TableCell>
                        {(() => {
                          if (payment.studentName) return payment.studentName;
                          const ar = payment.accountReference;
                          if (ar) {
                            const m = /^Fee-(.+)$/i.exec(ar);
                            if (m) {
                              const rn = m[1];
                              const nm = regNameMap[rn];
                              return nm ? `${nm} (${rn})` : `Reg ${rn}`;
                            }
                          }
                          return "—";
                        })()}
                      </TableCell>
                      <TableCell className="font-bold">
                        KES {Number(payment.amount || 0).toLocaleString()}
                      </TableCell>
                      <TableCell>{providerLabel(payment.provider)}</TableCell>
                      <TableCell>{getStatusBadge(payment.status)}</TableCell>
                      <TableCell>
                        {payment.transactionDate?.toDate
                          ? new Date(payment.transactionDate.toDate()).toISOString().slice(0, 10)
                          : "—"}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{payment.reference || payment.accountReference || "—"}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm">
                            <Receipt className="h-3 w-3" />
                          </Button>
                          <Button variant="outline" size="sm">View</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Payment Processing */}
      <Card>
        <CardHeader>
          <CardTitle>Bulk Payment Processing</CardTitle>
          <CardDescription>Process multiple payments at once</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Bulk Payment Import</h3>
            <p className="text-muted-foreground mb-4">Upload CSV file to process multiple payments</p>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Upload Payment File
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentProcessing;